import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { DashboardPageFormField, DashboardPageFormToggleSection } from '@shigo-ui/dashboard-ui';
import {
  Checkbox,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@shigo-ui/ui';

import { dashboardT } from '../../../../../lib/messages';
import { PlacementIvtIntegrationType, PlacementIvtType } from '../../model';
import type { PlacementFormValue } from '../../placementFormSchema';

export function PlacementRequestIvtFields() {
  'use no memo';
  const { control, register, setValue, formState } = useFormContext<PlacementFormValue>();
  const upstreamCapEnabled = useWatch({ control, name: 'upstreamCpmCapEnabled' });

  return (
    <>
      <section className="border-input grid gap-3 rounded-md border p-4">
        <h2 className="text-sm font-semibold">{dashboardT('placements.requestSettings')}</h2>
        <div className="grid gap-3 md:grid-cols-2">
          {(
            [
              ['passToDemandAppData', 'placements.passToDemandAppData'],
              ['passToDemandAppStoreIds', 'placements.passToDemandAppStoreIds'],
              ['rewriteVastEnabled', 'placements.rewriteVast'],
            ] as const
          ).map(([name, label]) => (
            <Controller
              key={name}
              control={control}
              name={name}
              render={({ field }) => (
                <Label className="flex items-center gap-2 text-sm font-normal">
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={(checked) => {
                      field.onChange(checked === true);
                    }}
                  />
                  {dashboardT(label)}
                </Label>
              )}
            />
          ))}
        </div>
        <DashboardPageFormField
          label={dashboardT('placements.vastVersion')}
          htmlFor="placement-vast-version"
        >
          <Input
            id="placement-vast-version"
            className="max-w-48"
            {...register('rewriteVastVersion')}
          />
        </DashboardPageFormField>
      </section>

      <Controller
        control={control}
        name="ivtEnabled"
        render={({ field }) => (
          <DashboardPageFormToggleSection
            label={dashboardT('placements.ivt')}
            checked={field.value}
            onCheckedChange={field.onChange}
          >
            <div className="grid gap-5 md:grid-cols-2">
              <Controller
                control={control}
                name="ivtType"
                render={({ field: ivtField, fieldState }) => (
                  <DashboardPageFormField
                    label={dashboardT('placements.ivtType')}
                    required
                    error={fieldState.error?.message}
                  >
                    <Select value={ivtField.value} onValueChange={ivtField.onChange}>
                      <SelectTrigger aria-invalid={Boolean(fieldState.error)}>
                        <SelectValue placeholder={dashboardT('placements.select')} />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.values(PlacementIvtType).map((type) => (
                          <SelectItem key={type} value={type}>
                            {type}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </DashboardPageFormField>
                )}
              />
              <Controller
                control={control}
                name="ivtIntegrationType"
                render={({ field: ivtField, fieldState }) => (
                  <DashboardPageFormField
                    label={dashboardT('placements.ivtIntegrationType')}
                    required
                    error={fieldState.error?.message}
                  >
                    <Select value={ivtField.value} onValueChange={ivtField.onChange}>
                      <SelectTrigger aria-invalid={Boolean(fieldState.error)}>
                        <SelectValue placeholder={dashboardT('placements.select')} />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.values(PlacementIvtIntegrationType).map((type) => (
                          <SelectItem key={type} value={type}>
                            {type}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </DashboardPageFormField>
                )}
              />
              {(
                [
                  'ivtSamplingPercentage',
                  'ivtGeneralRejectPercentage',
                  'ivtSophisticatedRejectPercentage',
                ] as const
              ).map((name) => (
                <DashboardPageFormField
                  key={name}
                  label={dashboardT(`placements.${name}`)}
                  error={formState.errors[name]?.message}
                >
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    aria-invalid={Boolean(formState.errors[name])}
                    {...register(name)}
                  />
                </DashboardPageFormField>
              ))}
            </div>
          </DashboardPageFormToggleSection>
        )}
      />

      <DashboardPageFormToggleSection
        label={dashboardT('placements.upstreamCpmCap')}
        checked={upstreamCapEnabled}
        onCheckedChange={(checked) => {
          setValue('upstreamCpmCapEnabled', checked, { shouldDirty: true });
        }}
      >
        <div className="grid gap-5 md:grid-cols-2">
          {(['upstreamCpmCapMinCpm', 'upstreamCpmCapMaxCpm'] as const).map((name) => (
            <DashboardPageFormField
              key={name}
              label={dashboardT(`placements.${name}`)}
              error={formState.errors[name]?.message}
            >
              <Input
                type="number"
                min="0"
                aria-invalid={Boolean(formState.errors[name])}
                {...register(name)}
              />
            </DashboardPageFormField>
          ))}
        </div>
      </DashboardPageFormToggleSection>
    </>
  );
}
